import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Alert, Button, Container, Text } from '@groupelacentrale/contact-storybook';
import { FormikErrors, useFormik } from 'formik';
import { useApp, useDeposit } from 'providers';
import {
  ApiError,
  VehicleMatch,
  VehicleMatchItem,
  VehicleType,
  VehicleVersion,
  VehicleVersionBodyFieldsList,
  VehicleVersionFields,
  VehicleVersionFieldsList,
} from 'types';
import { VehicleBodyFormView } from './VehicleBodyFormView';
import { VehicleStateFormView } from './VehicleStateFormView';
import { VehicleVersionFormView } from './VehicleVersionFormView';
import { vehicleVersionChoiceSelectionValidationSchema } from './validationSchema';
import { Tracking, VehicleReferentialService } from 'api';
import { DepositVehicle } from 'types/Deposit';
import {
  createVehicleMatchLists,
  createVehicleSearchOptionsLists,
  emptyFullVehicleVersion,
  getEligibleVersionsForFilter,
  getValidDefaultVehicleVersion,
  getValidEligibleFields,
  selectFilterBySelectedVersion,
} from 'utils/vehicleVersion';
import {
  VEHICLE_SPECIFIC_INTERIOR_COLOR,
  VEHICLE_SPECIFIC_EXTERIOR_COLOR,
  VEHICLE_PAINT_TYPE,
  VEHICLE_SEAT_MATERIAL,
} from 'utils/vehicleBody';
import './VehicleVersionForm.scss';
import { useMountEffect } from 'hooks/use-mount-effect';
import { useLoading } from 'hooks';
import { Loader } from 'components';

interface Props {
  defaultVehicle: DepositVehicle;
  vehicleType: VehicleType;
  isMatched: boolean;
  defaultVehicleMatchList?: VehicleMatch[];
}

export const vehicleVersionBodyFieldsList: VehicleVersionBodyFieldsList = {
  internalColor: VEHICLE_SPECIFIC_INTERIOR_COLOR,
  externalColor: VEHICLE_SPECIFIC_EXTERIOR_COLOR,
  paintType: VEHICLE_PAINT_TYPE,
  seatMaterial: VEHICLE_SEAT_MATERIAL,
};

const emptyOptions: VehicleVersionFieldsList = {
  make: [],
  model: [],
  category: [],
  version: [],
  energy: [],
  doors: [],
  gearbox: [],
  fiscalHorsePower: [],
  motorizationFull: [],
  trimLevel: [],
  cubic: [],
};

// Refs
const refsValues: Record<keyof VehicleVersion, React.RefObject<HTMLDivElement>> = {
  firstCirculationDate: React.createRef<HTMLDivElement>(),
  make: React.createRef<HTMLDivElement>(),
  model: React.createRef<HTMLDivElement>(),
  category: React.createRef<HTMLDivElement>(),
  version: React.createRef<HTMLDivElement>(),
  energy: React.createRef<HTMLDivElement>(),
  doors: React.createRef<HTMLDivElement>(),
  gearbox: React.createRef<HTMLDivElement>(),
  fiscalHorsePower: React.createRef<HTMLDivElement>(),
  motorizationFull: React.createRef<HTMLDivElement>(),
  trimLevel: React.createRef<HTMLDivElement>(),
  cubic: React.createRef<HTMLDivElement>(),
  imported: React.createRef<HTMLDivElement>(),
  firstHand: React.createRef<HTMLDivElement>(),
  mileage: React.createRef<HTMLDivElement>(),
  condition: React.createRef<HTMLDivElement>(),
  internalColor: React.createRef<HTMLDivElement>(),
  externalColor: React.createRef<HTMLDivElement>(),
  paintType: React.createRef<HTMLDivElement>(),
  seatMaterial: React.createRef<HTMLDivElement>(),
  referencePro: React.createRef<HTMLDivElement>(),
};

export const VehicleVersionForm: React.FC<Props> = ({
  defaultVehicle,
  vehicleType,
  isMatched,
  defaultVehicleMatchList = [],
}) => {
  const { referencePro, setVehicleVersion } = useDeposit();
  const { user } = useApp();
  const { isLoading, toggleLoading } = useLoading(true);

  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [selectedVersionVehicle, setSelectedVersionVehicle] = useState<VehicleMatchItem | undefined>(undefined);
  const [vehicleMatchList, setVehicleMatchList] = useState<VehicleMatch[]>([]);
  const [options, setOptions] = useState<VehicleVersionFieldsList>(emptyOptions);
  const [eligibleVersions, setEligibleVersions] = useState<VehicleVersionFieldsList>(emptyOptions);

  const initialValues = useMemo(
    () => ({
      ...emptyFullVehicleVersion,
      ...defaultVehicle,
      ...(isMatched &&
        !defaultVehicle.version &&
        getValidDefaultVehicleVersion(defaultVehicle, createVehicleMatchLists(defaultVehicleMatchList))),
      referencePro,
    }),
    [defaultVehicle, defaultVehicleMatchList, isMatched, referencePro],
  );

  useMountEffect(() => {
    (async () => {
      let newVehicleMatchList: VehicleMatch[] = [];
      const { firstCirculationDate, make, model } = initialValues;

      if (isMatched) {
        newVehicleMatchList = createVehicleMatchLists(defaultVehicleMatchList);
      } else {
        if (firstCirculationDate && handleFirstCirculationDateChange) {
          await handleFirstCirculationDateChange(firstCirculationDate);

          if (make && handleMakeChange) {
            await handleMakeChange(firstCirculationDate, make);

            if (model && handleModelChange) {
              newVehicleMatchList = await VehicleReferentialService.fetchVersions(
                vehicleType,
                firstCirculationDate,
                make,
                model,
              );
            }
          }
        }
      }

      handleVehicleMatchList(newVehicleMatchList, { ...initialValues, version: undefined });
      toggleLoading();
    })();
  });

  const handleVehicleMatchList = useCallback(
    (newVehicleMatchList: VehicleMatch[], filteredVehicleVersion: VehicleVersionFields) => {
      setVehicleMatchList(newVehicleMatchList);
      setOptions(oldOptions => ({
        ...createVehicleSearchOptionsLists(newVehicleMatchList),
        ...(oldOptions.make.length > 0 && { make: oldOptions.make }),
        ...(oldOptions.model.length > 0 && { model: oldOptions.model }),
      }));
      setEligibleVersions(getEligibleVersionsForFilter(newVehicleMatchList, filteredVehicleVersion));
    },
    [],
  );

  // Declutched
  const handleFirstCirculationDateChange = useCallback(
    async (value: Date) => {
      try {
        const newMakes = await VehicleReferentialService.fetchMakes(vehicleType, value);
        setOptions({ ...emptyOptions, make: newMakes });
        setEligibleVersions(emptyOptions);
      } catch (e) {
        setErrorMessage((e as ApiError).message);
      }
    },
    [vehicleType],
  );

  // Declutched
  const handleMakeChange = useCallback(
    async (firstCirculationDate: Date, value: string) => {
      try {
        if (firstCirculationDate) {
          const models = await VehicleReferentialService.fetchModels(vehicleType, firstCirculationDate, value);
          setOptions(oldOptions => ({ ...emptyOptions, make: oldOptions.make, model: models }));
        }
        setEligibleVersions(emptyOptions);
      } catch (e) {
        setErrorMessage((e as ApiError).message);
      }
    },
    [vehicleType],
  );

  // Declutched
  const handleModelChange = useCallback(
    async (firstCirculationDate: Date, make: string, value: string) => {
      try {
        if (firstCirculationDate && make) {
          const newVehicleMatchList = await VehicleReferentialService.fetchVersions(
            vehicleType,
            firstCirculationDate,
            make,
            value,
          );

          handleVehicleMatchList(newVehicleMatchList, { make, model: value });
        }
      } catch (e) {
        setErrorMessage((e as ApiError).message);
      }
    },
    [vehicleType, handleVehicleMatchList],
  );

  const handleFilterableFieldChange = useCallback(
    (filteredValues: VehicleVersionFields, oldValues: VehicleVersionFields) => {
      const validEligibleFields = getValidEligibleFields(vehicleMatchList, filteredValues, oldValues);
      const validFieldsList = getEligibleVersionsForFilter(vehicleMatchList, validEligibleFields);
      setEligibleVersions(validFieldsList);
      return validEligibleFields;
    },
    [vehicleMatchList],
  );

  const handleVersionChange = useCallback(
    (value: string) => {
      setSelectedVersionVehicle(vehicleMatchList.find(match => match.item.version === value)?.item);
      const selectedVersionFields = selectFilterBySelectedVersion(vehicleMatchList, value);
      setEligibleVersions(
        getEligibleVersionsForFilter(vehicleMatchList, { ...selectedVersionFields, version: undefined }),
      );
      return selectedVersionFields;
    },
    [vehicleMatchList],
  );

  const formikBag = useFormik<VehicleVersion>({
    enableReinitialize: true,
    initialValues,
    validationSchema: vehicleVersionChoiceSelectionValidationSchema[vehicleType],
    onSubmit: (vehicleVersion, { setErrors, setSubmitting }) => {
      const errors: FormikErrors<VehicleVersion> = {};
      if (!selectedVersionVehicle || vehicleVersion.version !== selectedVersionVehicle.version) {
        errors.version = 'Veuillez sélectionner une version.';
      }

      if (Object.keys(errors).length !== 0) {
        setErrors(errors);
        setSubmitting(false);
        return;
      }

      if (selectedVersionVehicle?.versionId) {
        setVehicleVersion(
          selectedVersionVehicle.versionId.toString(),
          { ...selectedVersionVehicle, ...vehicleVersion },
          vehicleVersion.referencePro,
        ).catch(e => {
          if (e.code === 'REF_PRO_DUPLICATE') {
            errors.referencePro = 'La référence pro est déjà utilisée';
            setErrors(errors);
          } else {
            e.message && setErrorMessage(e.message);
          }
          setSubmitting(false);
        });
      }
    },
  });

  const { errors, isSubmitting } = formikBag;

  useEffect(() => {
    if (isSubmitting && Object.keys(errors).length > 0) {
      Tracking.sendCurrentPageError();
      const key = Object.keys(errors)[0]!;
      refsValues[key as keyof VehicleVersion].current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [errors, isSubmitting]);

  return isLoading ? (
    <Loader />
  ) : (
    <Container shadowed paddings="mediumP" backgroundColor="bgWhite">
      <form name="vehicle-version-choice" className="vehicle-version" onSubmit={formikBag.handleSubmit}>
        {errorMessage ? (
          <Alert type="error">{errorMessage}</Alert>
        ) : (
          <>
            <VehicleVersionFormView
              vehicleType={vehicleType}
              isMatched={isMatched}
              vehicleMatchList={vehicleMatchList}
              vehicleVersionFieldsList={options}
              eligibleVehicleVersionFieldsList={eligibleVersions}
              refs={refsValues}
              handleFilterableFieldChange={handleFilterableFieldChange}
              handleVersionChange={handleVersionChange}
              {...(!isMatched && { handleFirstCirculationDateChange, handleMakeChange, handleModelChange })}
              {...formikBag}
            />
            <VehicleStateFormView isPro={user?.isPro} refs={refsValues} {...formikBag} />
            <VehicleBodyFormView
              vehicleType={vehicleType}
              vehicleVersionBodyFieldsList={vehicleVersionBodyFieldsList}
              refs={refsValues}
              {...formikBag}
            />
          </>
        )}
        <div className="actions">
          <Text>* Champ obligatoire</Text>
          <Button
            label="Valider et continuer"
            type="submit"
            disabled={formikBag.isSubmitting || errorMessage}
            loading={formikBag.isSubmitting}
          />
        </div>
      </form>
    </Container>
  );
};
